// src/components/SalidaEntradaPatrones/NuevaSalida.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Table,
  Badge
} from 'react-bootstrap';
import { FaArrowLeft, FaCheckCircle, FaTimes, FaSync } from 'react-icons/fa';
import { useTable, useSortBy, usePagination } from 'react-table';
import { createMovimientoBulk } from '../../api/movimientosService';
import { getDataList } from '../../api/common';
import { getPatronesDisponibles } from '../../api/patronesService';
import { useAuth } from '../../hooks/useAuth';
import { Alertassw } from '../Alertas/Alertassw';
import OtSelector from './OtSelector';
import { todayColombia } from '../../utils/fechasColombia';

// Referencia estable para evitar re-renders infinitos cuando equiposOt no se pasa
const EMPTY_EQUIPOS_OT = [];

/**
 * Formulario para registrar salida de múltiples patrones
 */
const NuevaSalida = ({ onBack, onSuccess, otDefault = null, equiposOt = EMPTY_EQUIPOS_OT }) => {
  const { userData } = useAuth();
  const [formData, setFormData] = useState({
    patronIds: [],
    otIds: [], // Array de objetos: [{otId, cliente, numeroOt, ciudad}]
    responsibleUserId: userData?._id || '',
    dueDate: '', // Fecha límite para cerrar el movimiento (obligatorio)
    notes: ''
  });

  const [usuarios, setUsuarios] = useState([]);
  const [patrones, setPatrones] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [loadingPatrones, setLoadingPatrones] = useState(true);
  const [errors, setErrors] = useState({});
  const [filtros, setFiltros] = useState({
    inventario: '',
    item: '',
    marca: '',
    serie: '',
    estado: '',
    disponibilidad: 'todos'
  });

  useEffect(() => {
    cargarUsuarios();
    cargarPatrones();
  }, []);

  // IDs de patrones sugeridos para los equipos de esta OT
  const patronesRecomendados = useMemo(() => {
    return new Set(
      (equiposOt || []).flatMap(e =>
        (e.equipo?.item?.patronesUtilizados ?? []).flat()
      )
    );
  }, [equiposOt]);

  // Agregar OT por defecto al estado cuando el componente se monta
  useEffect(() => {
    if (otDefault && otDefault._id) {
      // Extraer correctamente el nombre del cliente (puede ser objeto o string)
      const clienteName = otDefault.cliente?.nombre || otDefault.cliente || '';
      const ciudadOt = otDefault.ciudad || otDefault.cliente?.ciudad || '';
      
      const otFormateada = {
        otId: otDefault._id,
        cliente: clienteName,
        numeroOt: otDefault.numeroOrden || '',
        ciudad: ciudadOt
      };
      
      // Solo agregar si no existe ya en el array
      setFormData(prev => {
        const exists = prev.otIds.some(ot => ot.otId === otDefault._id);
        if (!exists) {
          return { ...prev, otIds: [otFormateada] };
        }
        return prev;
      });
    }
  }, [otDefault]);

  const cargarUsuarios = async () => {
    try {
      setLoadingUsuarios(true);
      /*const response = await getDataList('usuarios', {
        conditions: {
          activo: true,
          $or: [
            { rol: 'METROLOGIST' },
            { rol: 'ADMIN' },
            { rol: 'SUPER_ADMIN' }
          ]
        },
        sort: { nombre: 1 }
      });

      setUsuarios(response.data.data || []);*/
    } catch (error) {
      console.error('error al cargar usuarios:', error);
      Alertassw('error', 'No se pudieron cargar los usuarios disponibles', 'error');
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const cargarPatrones = async () => {
    try {
      setLoadingPatrones(true);
      // Cargar TODOS los patrones sin filtrar por disponibilidad
      const response = await getDataList('Patrones', {
                    tipoEquipo: 'Patrón'
                });
      setPatrones(response.data.data|| []);
    } catch (error) {
      console.error('Error al cargar patrones:', error);
      Alertassw('error', 'No se pudieron cargar los patrones disponibles', 'error');
    } finally {
      setLoadingPatrones(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (formData.patronIds.length === 0) {
      newErrors.patronIds = 'Debe seleccionar al menos un patrón';
    }

    if (formData.otIds.length === 0) {
      newErrors.otIds = 'Debe asignar al menos una Orden de Trabajo';
    }

    if (!formData.responsibleUserId) {
      newErrors.responsibleUserId = 'Debe seleccionar un responsable';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Debe seleccionar una fecha límite para el cierre';
    } else {
      // Validar que la fecha no sea anterior a hoy
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(formData.dueDate);
      if (selectedDate < today) {
        newErrors.dueDate = 'La fecha límite no puede ser anterior a hoy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Alertassw('error','Campos requeridos', 'Por favor completa todos los campos obligatorios', 'warning');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patronIds: formData.patronIds,
        otIds: formData.otIds, // Ya tiene el formato correcto: [{otId, cliente, numeroOt, ciudad}]
        responsibleUserId: formData.responsibleUserId,
        dueDate: formData.dueDate, // Fecha límite para recordatorio de cierre
        notes: formData.notes.trim() || undefined
      };

      const response = await createMovimientoBulk(payload);

      if (response.ok) {
        Alertassw('success',
          'Salida Registrada',
          `${response.data.totalPatrones} patrón(es) registrado(s) exitosamente. Consecutivo: ${response.data.groupConsecutive}`,
          'success'
        );

        // Limpiar formulario
        setFormData({
          patronIds: [],
          otIds: [],
          responsibleUserId: userData?._id || '',
          dueDate: '',
          notes: ''
        });
        setErrors({});

        // Callback de éxito
        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      console.error('Error al crear salida:', error);
      const errorMessage =
        error.response?.data?.message ||
        'No se pudo registrar la salida de patrones';
      Alertassw('error', errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      patronIds: [],
      otIds: [],
      responsibleUserId: userData?._id || '',
      dueDate: '',
      notes: ''
    });
    setErrors({});
    if (onBack) {
      onBack();
    }
  };

  const handleTogglePatron = (patronId) => {
    // Buscar el patrón para verificar si está en servicio
    const patron = patrones.find(p => p._id === patronId);
    if (patron && patron.inService) {
      // No permitir seleccionar patrones en servicio
      return;
    }
    
    setFormData((prev) => {
      const isSelected = prev.patronIds.includes(patronId);
      
      if (isSelected) {
        // Deseleccionar
        return {
          ...prev,
          patronIds: prev.patronIds.filter((id) => id !== patronId)
        };
      } else {
        // Seleccionar (solo si no está ya seleccionado)
        return {
          ...prev,
          patronIds: [...prev.patronIds, patronId]
        };
      }
    });
  };

  const handleSelectAll = () => {
    // Obtener solo patrones disponibles (no en servicio)
    const patronesDisponibles = patrones.filter(p => !p.inService);
    const idsDisponibles = patronesDisponibles.map(p => p._id);
    
    if (formData.patronIds.length === idsDisponibles.length && idsDisponibles.length > 0) {
      // Deseleccionar todos
      setFormData({ ...formData, patronIds: [] });
    } else {
      // Seleccionar solo los disponibles
      setFormData({ ...formData, patronIds: idsDisponibles });
    }
  };

  // Configuración de columnas para react-table
  const columns = useMemo(
    () => [
      {
        Header: () => {
          const patronesDisponibles = patrones.filter(p => !p.inService);
          return (
            <input
              type="checkbox"
              checked={
                patronesDisponibles.length > 0 &&
                formData.patronIds.length === patronesDisponibles.length
              }
              onChange={handleSelectAll}
              disabled={loadingPatrones || patronesDisponibles.length === 0}
            />
          );
        },
        id: 'selection',
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={formData.patronIds.includes(row.original._id)}
            onChange={() => handleTogglePatron(row.original._id)}
            disabled={row.original.inService}
          />
        ),
        disableSortBy: true
      },
      {
        Header: 'Disponibilidad',
        accessor: 'inService',
        Cell: ({ value }) => (
          value ? (
            <Badge bg="danger" className="text-white" title="Equipo en servicio">
              🚫 En Servicio
            </Badge>
          ) : (
            <Badge bg="success" className="text-white" title="Disponible para salida">
              ✓ Disponible
            </Badge>
          )
        )
      },
      {
        Header: 'Sugerido',
        id: 'recomendado',
        disableSortBy: true,
        Cell: ({ row }) => (
          patronesRecomendados.has(row.original._id)
            ? <Badge bg="warning" text="dark">★ Sugerido</Badge>
            : null
        )
      },
      {
        Header: 'Inventario',
        accessor: 'inventario',
        Cell: ({ value }) => (
          <strong className="text-primary">{value}</strong>
        )
      },
      {
        Header: 'Equipo',
        accessor: 'item',
      },
      {
        Header: 'Marca',
        accessor: 'marca'
      },
            {
        Header: 'Modelo',
        accessor: 'modelo'
      },
      {
        Header: 'Serie',
        accessor: 'serie'
      },
      {
        Header: 'Estado',
        accessor: 'estado',
        Cell: ({ value }) => (
          <Badge
            bg={value === 'Activo' ? 'success' : 'secondary'}
            className="text-white"
          >
            {value}
          </Badge>
        )
      }
    ],
    [patrones, formData.patronIds, loadingPatrones, patronesRecomendados]
  );

  // Función para filtrar patrones
  const patronesFiltrados = useMemo(() => {
    return patrones.filter((patron) => {
      const matchInventario = filtros.inventario
        ? patron.inventario?.toLowerCase().includes(filtros.inventario.toLowerCase())
        : true;
      const matchItem = filtros.item
        ? patron.item?.toLowerCase().includes(filtros.item.toLowerCase())
        : true;
      const matchMarca = filtros.marca
        ? patron.marca?.toLowerCase().includes(filtros.marca.toLowerCase())
        : true;
     const matchModelo = filtros.modelo
        ? patron.modelo?.toLowerCase().includes(filtros.modelo.toLowerCase())
        : true;
      const matchSerie = filtros.serie
        ? patron.serie?.toLowerCase().includes(filtros.serie.toLowerCase())
        : true;
      const matchEstado = filtros.estado
        ? patron.estado?.toLowerCase().includes(filtros.estado.toLowerCase())
        : true;

      const matchDisponibilidad = filtros.disponibilidad === 'disponible'
        ? !patron.inService
        : filtros.disponibilidad === 'en-servicio'
        ? patron.inService
        : true;

      return matchInventario && matchItem && matchMarca && matchModelo && matchSerie && matchEstado && matchDisponibilidad;
    });
  }, [patrones, filtros]);

  // Datos para la tabla
  const data = useMemo(() => patronesFiltrados, [patronesFiltrados]);

  // Configuración de react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 }
    },
    useSortBy,
    usePagination
  );

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" onClick={handleCancel}>
              <FaArrowLeft />
            </Button>
            <div>
              <h2 className="text-primary fw-bold mb-1">
                📤 Nueva Salida de Patrones
              </h2>
              <p className="text-muted mb-0">
                Registra la salida de equipos patrón para trabajo en campo
              </p>
            </div>
          </div>
        </Col>
      </Row>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={12} className="mx-auto">
            {/* Información del usuario */}
            {userData && (
              <Alert variant="info" className="mb-4">
                <strong>Usuario actual:</strong> {userData.nombre} {userData.apellido}
                <br />
                <small>
                  Por defecto, tú serás el responsable. Puedes cambiarlo si es necesario.
                </small>
              </Alert>
            )}

                        {/* Selección de OTs */}
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-info text-white w-100">
                <h5 className="mb-0">1. Asigna Órdenes de Trabajo *</h5>
              </Card.Header>
              <Card.Body className="w-100">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Órdenes de Trabajo asociadas
                    <span className="text-danger"> *</span>
                  </Form.Label>
                  <OtSelector
                    value={formData.otIds.map(ot => ot.otId)} // Pasar solo IDs al componente
                    onChange={(selectedIds, selectedOtsData) => { 
                      // Transformar los objetos OT completos al formato requerido
                      const formattedOts = selectedOtsData.map(ot => ({
                        otId: ot._id,
                        cliente: ot.cliente?.nombre || ot.cliente || '', // Manejar objeto o string
                        numeroOt: ot.numeroOrden || '',
                        ciudad: ot.cliente?.ciudad || ot.ciudad || ''
                      }));
                      setFormData({ ...formData, otIds: formattedOts });
                    }}
                    multiple={true}
                    disabled={loading}
                    otDefault={otDefault}
                  />
                  {errors.otIds && (
                    <div className="text-danger small mt-1">
                      {errors.otIds}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Debe asignar al menos una Orden de Trabajo para realizar la salida de patrones.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Selección de Patrones */}
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center w-100">
                <h5 className="mb-0">2 Selecciona los Patrones *</h5>
                <div className="d-flex gap-2 align-items-center">
                  <Badge bg="light" text="dark">
                    {formData.patronIds.length} seleccionado(s)
                  </Badge>
                  <Button
                    size="sm"
                    variant="light"
                    onClick={cargarPatrones}
                    disabled={loadingPatrones}
                  >
                    <FaSync className={loadingPatrones ? 'fa-spin' : ''} />
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="w-100">
                {loadingPatrones ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-3">Cargando patrones disponibles...</p>
                  </div>
                ) : patrones.length === 0 ? (
                  <Alert variant="warning">
                    <strong>No hay patrones disponibles</strong>
                    <br />
                    Todos los patrones están actualmente en servicio o no hay registros.
                  </Alert>
                ) : (
                  <>
                    {/* Filtros por columna */}
                    <Row className="mb-3">
                      <Col md={1}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Inventario</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar..."
                            value={filtros.inventario}
                            onChange={(e) =>
                              setFiltros({ ...filtros, inventario: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Equipo</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar..."
                            value={filtros.item}
                            onChange={(e) =>
                              setFiltros({ ...filtros, item: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Marca</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar..."
                            value={filtros.marca}
                            onChange={(e) =>
                              setFiltros({ ...filtros, marca: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                            <Form.Label className="small fw-bold">Modelo</Form.Label>
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Buscar..."
                                value={filtros.modelo}
                                onChange={(e) =>
                                    setFiltros({ ...filtros, modelo: e.target.value })
                                }
                            />
                        </Form.Group>
                        </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Serie</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar..."
                            value={filtros.serie}
                            onChange={(e) =>
                              setFiltros({ ...filtros, serie: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Estado</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="Buscar..."
                            value={filtros.estado}
                            onChange={(e) =>
                              setFiltros({ ...filtros, estado: e.target.value })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="small fw-bold">Disponibilidad</Form.Label>
                          <select
                            className="form-select form-select-sm"
                            value={filtros.disponibilidad}
                            onChange={e => setFiltros({ ...filtros, disponibilidad: e.target.value })}
                          >
                            <option value="todos">Todos</option>
                            <option value="disponible">Disponible</option>
                            <option value="en-servicio">En Servicio</option>
                          </select>
                        </Form.Group>
                      </Col>
                      <Col md={1} className="d-flex align-items-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() =>
                            setFiltros({
                              inventario: '',
                              item: '',
                              marca: '',
                              modelo: '',
                              serie: '',
                              estado: '',
                              disponibilidad: 'todos'
                            })
                          }
                          title="Limpiar filtros"
                        >
                          <FaTimes />
                        </Button>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <Table
                        {...getTableProps()}
                        striped
                        bordered
                        hover
                        size="sm"
                        className="mb-0"
                      >
                        <thead className="bg-light">
                          {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                              {headerGroup.headers.map((column) => (
                                <th
                                  {...column.getHeaderProps(
                                    !column.disableSortBy ? column.getSortByToggleProps() : {}
                                  )}
                                  style={{ 
                                    cursor: column.disableSortBy ? 'default' : 'pointer',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                  {column.render('Header')}
                                  {!column.disableSortBy && (
                                    <span>
                                      {column.isSorted
                                        ? column.isSortedDesc
                                          ? ' 🔽'
                                          : ' 🔼'
                                        : ' ⬍'}
                                    </span>
                                  )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                          {page.map((row) => {
                            prepareRow(row);
                            return (
                              <tr
                                {...row.getRowProps()}
                                onClick={() => !row.original.inService && handleTogglePatron(row.original._id)}
                                className={
                                  formData.patronIds.includes(row.original._id)
                                    ? 'table-primary'
                                    : row.original.inService
                                    ? 'table-secondary'
                                    : ''
                                }
                                style={{ 
                                  cursor: row.original.inService ? 'not-allowed' : 'pointer',
                                  opacity: row.original.inService ? 0.6 : 1
                                }}
                                title={row.original.inService ? 'Equipo en servicio - No disponible' : 'Click para seleccionar/deseleccionar'}
                              >
                                {row.cells.map((cell) => (
                                  <td 
                                    {...cell.getCellProps()}
                                    onClick={(e) => {
                                      // Si el click es en el checkbox, prevenir la propagación
                                      // para evitar doble toggle
                                      if (cell.column.id === 'selection') {
                                        e.stopPropagation();
                                      }
                                    }}
                                  >
                                    {cell.render('Cell')}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>

                    {/* Paginación */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        <small className="text-muted">
                          Mostrando {page.length} de {patronesFiltrados.length} patrones
                          {patronesFiltrados.length !== patrones.length && (
                            <span className="text-primary">
                              {' '}(filtrados de {patrones.length} totales)
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => gotoPage(0)}
                          disabled={!canPreviousPage}
                        >
                          {'<<'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => previousPage()}
                          disabled={!canPreviousPage}
                        >
                          {'<'}
                        </Button>
                        <span className="text-muted">
                          Página{' '}
                          <strong>
                            {pageIndex + 1} de {pageOptions.length}
                          </strong>
                        </span>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => nextPage()}
                          disabled={!canNextPage}
                        >
                          {'>'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => gotoPage(pageCount - 1)}
                          disabled={!canNextPage}
                        >
                          {'>>'}
                        </Button>
                        <Form.Select
                          size="sm"
                          value={pageSize}
                          onChange={(e) => setPageSize(Number(e.target.value))}
                          style={{ width: 'auto' }}
                        >
                          {[5, 10, 20, 50].map((size) => (
                            <option key={size} value={size}>
                              {size} por página
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>

                    {errors.patronIds && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        {errors.patronIds}
                      </Alert>
                    )}
                    
                    <Form.Text className="text-muted d-block mt-2">
                      💡 Selecciona uno o más patrones usando los checkboxes. 
                      Los patrones en servicio están deshabilitados y marcados en rojo.
                    </Form.Text>
                  </>
                )}
              </Card.Body>
            </Card>


            {/* Responsable 
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-success text-white w-100">
                <h5 className="mb-0">3. Responsable del Movimiento *</h5>
              </Card.Header>
              <Card.Body className="w-100">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Usuario responsable
                    <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.responsibleUserId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        responsibleUserId: e.target.value
                      })
                    }
                    disabled={loading || loadingUsuarios}
                    isInvalid={!!errors.responsibleUserId}
                  >
                    <option value="">Seleccionar usuario...</option>
                    {usuarios.map((usuario) => (
                      <option key={usuario._id} value={usuario._id}>
                        {usuario.nombre} {usuario.apellido} - {usuario.rol}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.responsibleUserId && (
                    <Form.Control.Feedback type="invalid">
                      {errors.responsibleUserId}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    Esta persona será responsable de los equipos durante el movimiento
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>*/}

            {/* Fecha Límite para Cierre */}
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-danger text-white w-100">
                <h5 className="mb-0">3. Fecha Límite para Cierre *</h5>
              </Card.Header>
              <Card.Body className="w-100">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Fecha estimada de cierre del movimiento
                    <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) =>
                      setFormData({ ...formData, dueDate: e.target.value })
                    }
                    disabled={loading}
                    isInvalid={!!errors.dueDate}
                    min={todayColombia()}
                  />
                  {errors.dueDate && (
                    <Form.Control.Feedback type="invalid">
                      {errors.dueDate}
                    </Form.Control.Feedback>
                  )}
                  <Form.Text className="text-muted">
                    📅 Define cuándo deben regresar los equipos. Si no se cierra el movimiento para esta fecha, 
                    se generará una notificación automática de recordatorio.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Notas */}
            <Card className="shadow-sm mb-4 w-100">
              <Card.Header className="bg-warning w-100">
                <h5 className="mb-0">4. Notas Adicionales (Opcional)</h5>
              </Card.Header>
              <Card.Body className="w-100">
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Observaciones o detalles del movimiento
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Ej: Ruta zona industrial, clientes cercanos, calibraciones programadas..."
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    disabled={loading}
                    maxLength={500}
                  />
                  <Form.Text className="text-muted">
                    {formData.notes.length}/500 caracteres
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            {/* Botones de acción */}
            <Card className="shadow-sm w-100">
              <Card.Body>
                <Row>
                  <Col className="d-flex justify-content-between">
                    <Button
                      variant="secondary"
                      size="lg"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <FaTimes className="me-2" />
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="lg"
                      type="submit"
                      disabled={loading || loadingUsuarios}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Registrando...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="me-2" />
                          Registrar Salida
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default NuevaSalida;
